import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { Database, Json } from './database.types';
import {
  labItemSchema,
  projectSchema,
  testimonialSchema,
  type LabItemInput,
  type ProjectInput,
  type SiteContent,
} from '@site/shared/content';

/**
 * Work: the site's case studies, their testimonials and the lab, as Desk edits
 * them. Two layers (migration 20261014120000_desk_work.sql):
 *
 *   working copy   case_studies / testimonials / lab_items. Saving changes
 *                  these and nothing else: the site does not move.
 *   published      site_publishes. "Publish" freezes the working copy into a
 *                  snapshot and asks Cloudflare to rebuild bitbew.com.
 *
 * A record's content is stored as `data` in exactly the shape the site reads
 * (src/shared/content.ts), so validating here with the site's own schema
 * means a save that passes cannot break the build.
 */

export type CaseStudyRow = Database['public']['Tables']['case_studies']['Row'];
export type TestimonialRow = Database['public']['Tables']['testimonials']['Row'];
export type LabRow = Database['public']['Tables']['lab_items']['Row'];
export type PublishRow = Database['public']['Tables']['site_publishes']['Row'];

/** Ids that would collide with Desk's own routes under /work. */
export const RESERVED_IDS = new Set(['new', 'lab']);

export const workKeys = {
  all: ['work'] as const,
  studies: ['work', 'studies'] as const,
  testimonials: ['work', 'testimonials'] as const,
  lab: ['work', 'lab'] as const,
  publish: ['work', 'publish'] as const,
  draft: ['work', 'draft'] as const,
};

/* ------------------------------------------------------------------ reads */

export function useCaseStudies() {
  return useQuery({
    queryKey: workKeys.studies,
    queryFn: async () => {
      const { data, error } = await supabase.from('case_studies').select('*').order('position').order('id');
      if (error) throw error;
      return data;
    },
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: workKeys.testimonials,
    queryFn: async () => {
      const { data, error } = await supabase.from('testimonials').select('*');
      if (error) throw error;
      return data;
    },
  });
}

export function useLabItems() {
  return useQuery({
    queryKey: workKeys.lab,
    queryFn: async () => {
      const { data, error } = await supabase.from('lab_items').select('*').order('position').order('id');
      if (error) throw error;
      return data;
    },
  });
}

export function useLatestPublish() {
  return useQuery({
    queryKey: workKeys.publish,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_publishes')
        .select('*')
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** What publishing right now would put on the site, computed by the database. */
export function useDraft() {
  return useQuery({
    queryKey: workKeys.draft,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('site_content_draft');
      if (error) throw error;
      return data as unknown as SiteContent;
    },
  });
}

/* ------------------------------------------------------ what would change */

export interface Change {
  kind: 'added' | 'removed' | 'edited' | 'moved' | 'redirect';
  what: string;
}

/** Plain-language differences between the live snapshot and the draft. */
export function diffContent(live: SiteContent | null, draft: SiteContent | null): Change[] {
  if (!live || !draft) return [];
  const changes: Change[] = [];

  const compare = <T extends { id: string; name: string }>(label: string, a: T[], b: T[]) => {
    const before = new Map(a.map((x, i) => [x.id, { x, i }]));
    const after = new Map(b.map((x, i) => [x.id, { x, i }]));
    for (const [id, { x }] of after) {
      if (!before.has(id)) changes.push({ kind: 'added', what: `${x.name} appears in ${label}` });
    }
    for (const [id, { x }] of before) {
      if (!after.has(id)) changes.push({ kind: 'removed', what: `${x.name} leaves ${label}` });
    }
    for (const [id, { x }] of after) {
      const old = before.get(id);
      if (old && deepChanged(old.x, x)) changes.push({ kind: 'edited', what: `${x.name} edited` });
    }
    // Order: one line with the new sequence, not one per row. Moving one item
    // shifts its neighbours too, and "J Charms moved" when J Charms was not
    // touched is noise.
    const common = (list: T[]) => list.filter((x) => before.has(x.id) && after.has(x.id)).map((x) => x.id);
    if (common(a).join() !== common(b).join()) {
      changes.push({ kind: 'moved', what: `New order in ${label}: ${b.map((x) => x.name).join(', ')}` });
    }
  };

  compare('Work', live.projects, draft.projects);
  compare('the Lab', live.lab, draft.lab);

  const liveQuotes = new Map(live.testimonials.map((t) => [t.project, JSON.stringify(t)]));
  for (const t of draft.testimonials) {
    const before = liveQuotes.get(t.project);
    if (before && before !== JSON.stringify(t) && !changes.some((c) => c.what.startsWith(t.company))) {
      changes.push({ kind: 'edited', what: `${t.company}'s testimonial edited` });
    }
  }

  const liveRedirects = new Set(live.redirects.map((r) => `${r.from}>${r.to}`));
  for (const r of draft.redirects) {
    if (!liveRedirects.has(`${r.from}>${r.to}`)) {
      changes.push({ kind: 'redirect', what: `${r.from} will redirect to ${r.to}` });
    }
  }
  return changes;
}

function deepChanged(a: unknown, b: unknown) {
  return JSON.stringify(sortDeep(a)) !== JSON.stringify(sortDeep(b));
}
function sortDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortDeep);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v)
        .sort()
        .map((k) => [k, sortDeep((v as Record<string, unknown>)[k])]),
    );
  }
  return v;
}

/* ---------------------------------------------------------------- writes */

function useInvalidateWork() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: workKeys.all });
}

/** New order for a list of rows: positions 10, 20, 30… in the given order. */
export function useReorder(table: 'case_studies' | 'lab_items') {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.all(
        ids.map((id, i) => supabase.from(table).update({ position: (i + 1) * 10 }).eq('id', id)),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSettled: invalidate,
  });
}

export function useSetVisible(table: 'case_studies' | 'lab_items') {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async ({ id, visible }: { id: string; visible: boolean }) => {
      const { error } = await supabase.from(table).update({ visible }).eq('id', id);
      if (error) throw error;
    },
    onSettled: invalidate,
  });
}

/** The site's `live` flag: false hides every outbound link, keeps the case study. */
export function useSetLinkLive() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async ({ row, live }: { row: CaseStudyRow; live: boolean }) => {
      const data = { ...(row.data as Record<string, Json>) };
      if (live) delete data.live;
      else data.live = false;
      const { error } = await supabase.from('case_studies').update({ data }).eq('id', row.id);
      if (error) throw error;
    },
    onSettled: invalidate,
  });
}

export interface TestimonialDraft {
  quote: string;
  company: string;
  descriptor: string;
}

/**
 * Save one case study and its testimonial. `project` is validated against the
 * site's own schema first; a save that passes here cannot fail the build.
 */
export function useSaveCaseStudy() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async ({
      project,
      isNew,
      testimonial,
    }: {
      project: ProjectInput;
      isNew: boolean;
      testimonial: TestimonialDraft | null;
    }) => {
      const parsed = projectSchema.safeParse(project);
      if (!parsed.success) throw new ValidationError(parsed.error.issues);
      const { id, slug, testimonial: _drop, ...data } = parsed.data;

      if (isNew) {
        const { data: last } = await supabase
          .from('case_studies')
          .select('position')
          .order('position', { ascending: false })
          .limit(1)
          .maybeSingle();
        const { error } = await supabase
          .from('case_studies')
          .insert({ id, slug, data: data as Json, visible: false, position: (last?.position ?? 0) + 10 });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('case_studies').update({ slug, data: data as Json }).eq('id', id);
        if (error) throw error;
      }

      if (testimonial) {
        const row = { id: `t-${id}`, case_study_id: id, ...testimonial };
        const check = testimonialSchema.safeParse({ ...testimonial, id: row.id, project: id });
        if (!check.success) throw new ValidationError(check.error.issues, 'testimonial');
        const { error } = await supabase.from('testimonials').upsert(row, { onConflict: 'case_study_id' });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('testimonials').delete().eq('case_study_id', id);
        if (error) throw error;
      }
      return id;
    },
    onSettled: invalidate,
  });
}

/** Only a case study that was never on the site may be deleted; a published one is hidden instead. */
export function useDeleteCaseStudy() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async (row: CaseStudyRow) => {
      if (row.first_published_at) throw new Error('This case study has been on the site. Hide it instead.');
      const { error } = await supabase.from('case_studies').delete().eq('id', row.id);
      if (error) throw error;
    },
    onSettled: invalidate,
  });
}

export function useSaveLabItem() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async ({ item, isNew }: { item: LabItemInput; isNew: boolean }) => {
      const parsed = labItemSchema.safeParse(item);
      if (!parsed.success) throw new ValidationError(parsed.error.issues);
      const { id, ...data } = parsed.data;
      if (isNew) {
        const { data: last } = await supabase
          .from('lab_items')
          .select('position')
          .order('position', { ascending: false })
          .limit(1)
          .maybeSingle();
        const { error } = await supabase
          .from('lab_items')
          .insert({ id, data: data as Json, visible: false, position: (last?.position ?? 0) + 10 });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('lab_items').update({ data: data as Json }).eq('id', id);
        if (error) throw error;
      }
      return id;
    },
    onSettled: invalidate,
  });
}

export function usePublish() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: async (note: string) => {
      const { data, error } = await supabase.functions.invoke('desk-publish', { body: { note } });
      if (error || !data?.ok) {
        let detail = data?.error as string | undefined;
        if (!detail && error && 'context' in error) {
          detail = await (error.context as Response)
            .json()
            .then((j) => j.error)
            .catch(() => undefined);
        }
        throw new Error(detail ?? error?.message ?? 'The publish failed.');
      }
      return data as { id: number; deploy: 'triggered' | 'failed' | 'not_configured'; detail: string };
    },
    onSettled: invalidate,
  });
}

/** Which publish bitbew.com is serving right now (written into every build). */
export async function liveContentVersion(): Promise<number | null> {
  try {
    const res = await fetch(`https://bitbew.com/content-version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.publishId === 'number' ? data.publishId : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------ validation */

/** zod issues, kept by field path so the editor can show each next to its input. */
export class ValidationError extends Error {
  readonly byPath: Map<string, string>;
  constructor(issues: { path: (string | number)[]; message: string }[], prefix?: string) {
    super('Some fields need attention before this can be saved.');
    this.byPath = new Map();
    for (const i of issues) {
      const key = [prefix, ...i.path].filter((p) => p !== undefined).join('.');
      if (!this.byPath.has(key)) this.byPath.set(key, i.message);
    }
  }
}

/* -------------------------------------------------------------- screenshots */

export interface ProcessedShot {
  webp: Blob;
  jpg: Blob;
  width: number;
  height: number;
  previewUrl: string;
}

/**
 * What scripts/gen-images.mjs does to a client screenshot, in the browser:
 * keep the top of a full-page capture at 2.5:1 (four frame-heights of the
 * browser frame's 16:10, which is what lets the frame pan through it on
 * scroll), resize to 1400 wide, and encode WebP and JPEG at one quality.
 * The quality is the founder's call per image: J Charms, wall-to-wall product
 * photography, needed 68 where flat marketing pages are fine at 78.
 */
export async function processScreenshot(file: File, quality: number): Promise<ProcessedShot> {
  const WIDTH = 1400;
  const RATIO = 2.5;
  const bitmap = await createImageBitmap(file);
  const cropH = Math.min(Math.round(bitmap.width * RATIO), bitmap.height);
  const outH = Math.round((WIDTH / bitmap.width) * cropH);
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot process images.');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, bitmap.width, cropH, 0, 0, WIDTH, outH);
  bitmap.close();
  const encode = (type: string) =>
    new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(`Could not encode ${type}`))), type, quality / 100),
    );
  const [webp, jpg] = await Promise.all([encode('image/webp'), encode('image/jpeg')]);
  return { webp, jpg, width: WIDTH, height: outH, previewUrl: URL.createObjectURL(webp) };
}

/**
 * Upload both encodings. The file name carries a hash of the WebP, so a
 * replaced screenshot gets a new URL and no cache anywhere can serve the old
 * one. Returns the site path (no extension) for the case study's `image`.
 */
export async function uploadScreenshot(id: string, shot: ProcessedShot): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await shot.webp.arrayBuffer());
  const hash = Array.from(new Uint8Array(digest).slice(0, 4), (b) => b.toString(16).padStart(2, '0')).join('');
  const base = `work/${id}-${hash}`;
  for (const [ext, blob, type] of [
    ['webp', shot.webp, 'image/webp'],
    ['jpg', shot.jpg, 'image/jpeg'],
  ] as const) {
    const { error } = await supabase.storage
      .from('site-media')
      .upload(`${base}.${ext}`, blob, { contentType: type, upsert: true, cacheControl: '31536000' });
    if (error) throw error;
  }
  return `/media/${base}`;
}

/** Where an image path can be previewed from inside Desk. */
export function imagePreviewUrl(image: string, ext: 'webp' | 'jpg' = 'webp') {
  if (image.startsWith('/media/')) {
    const { data } = supabase.storage.from('site-media').getPublicUrl(`${image.slice('/media/'.length)}.${ext}`);
    return data.publicUrl;
  }
  return `https://bitbew.com${image}.${ext}`;
}
